import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { CatalogClient, Feature } from 'src/WebApiController';

export const GROUP_INVOICE_CATALOG_QUERY_KEY = 'CatalogClient.getCatalogSlim';

/**
 * POS-8630: Batch-resolves the catalog (events + venues) for the member sales of a
 * group invoice via a SINGLE CatalogClient.getCatalogSlim call, so the grouped
 * "Print Invoice" download can fill in event name / date / venue per order-detail
 * row (member Sales carry only event IDs).
 *
 * Feature-gated on Feature.SalesGroupView — the same flag that gates the group
 * invoice path — because per repo convention new API hooks under src/hooks/api/
 * are feature-gated, and this hook is only ever used by that gated download path.
 * The query is only enabled when the feature is on, there is at least one id to
 * resolve, and an active account is present; the single-sale path passes
 * `undefined` ids so it never fires.
 */
export function useGetGroupInvoiceCatalog(
  viagogoVirtualIds: string[] | undefined
) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const hasSalesGroupViewFeature = useUserHasFeature(Feature.SalesGroupView);

  // Stable, distinct, sorted id list so the queryKey and request are stable and
  // dedupe regardless of source ordering/duplication.
  const distinctIds = useMemo(
    () => Array.from(new Set(viagogoVirtualIds ?? [])).toSorted(),
    [viagogoVirtualIds]
  );

  const shouldQuery =
    hasSalesGroupViewFeature &&
    distinctIds.length > 0 &&
    !!activeAccountWebClientConfig?.activeAccountId;

  return useQuery({
    queryKey: [
      GROUP_INVOICE_CATALOG_QUERY_KEY,
      activeAccountWebClientConfig?.activeAccountId,
      distinctIds,
    ],
    async queryFn() {
      if (!shouldQuery) {
        return null;
      }
      try {
        return await new CatalogClient(
          activeAccountWebClientConfig
        ).getCatalogSlim(distinctIds);
      } catch (err: unknown) {
        trackError('CatalogClient.getCatalogSlim', err as ErrorTypes, {
          distinctIds,
        });
        throw err;
      }
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
  });
}
